"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import Image from "next/image";
import { sessionStore } from "@lib/sessionStore";
import { logError } from "@lib/logger";
import { LikedEpisode } from "@api/models";
/**
 * ひとりができる最大LIKE数
 */
const MAX_COUNT = 10;

type LocalStorageData = { likeCount: number; episodeId: string; expire: number };

function setCountToLocalStorage(
  count: number,
  episodeId: string,
  localStorageData: LocalStorageData[],
) {
  if (window && window.localStorage) {
    window.localStorage.setItem(
      "episodeLikes",
      JSON.stringify([
        ...localStorageData.filter((d) => {
          return d.episodeId !== episodeId;
        }),
        // 一ヶ月保持
        { likeCount: count, episodeId: episodeId, expire: Date.now() + 30 * 24 * 60 * 60 * 1000 },
      ]),
    );
  }
}

export function LikeButton({
  totalLikeCount,
  episodeId,
}: {
  totalLikeCount: number;
  episodeId: string;
}) {
  const { session, sessionStatus } = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.getSnapshot,
    sessionStore.getServerSnapshot,
  );
  const [currentCount, setCurrentCount] = useState(-1);
  const [currentTotalCount, setCurrentTotalCount] = useState(totalLikeCount);

  const [isGetLocalStorage, setIsGetLocalStorage] = useState<boolean>(false);
  const [localStorageData, setLocalStorageData] = useState<LocalStorageData[]>([]);

  useEffect(() => {
    if (episodeId) {
      if (sessionStatus === "authenticated") {
        // ログインしている場合はサーバーから取得
        setCurrentCount(-1);
        fetch(`/api/episodes/${episodeId}/like`)
          .then((res) => {
            if (res.status !== 200) throw new Error();
            return res.json();
          })
          .then((res) => {
            const data = res.episodeLikes as LikedEpisode;
            setCurrentCount(data.likeNumber);
          })
          .catch((error) => {
            logError("Failed to fetch like count: " + error.message);
          });
      } else if (sessionStatus === "unauthenticated") {
        if (window && window.localStorage && !isGetLocalStorage) {
          const data: LocalStorageData[] = JSON.parse(
            window.localStorage.getItem("episodeLikes") || "[]",
          );

          const filteredData = data.filter((d) => d.expire > Date.now());
          setLocalStorageData(filteredData);
          setIsGetLocalStorage(true);
          // 期限切れしかなければ終了
          if (filteredData.length === 0) {
            setLocalStorageData(data);
            setCurrentCount(0);
            return;
          }
          const json = filteredData.filter((d) => d.episodeId === episodeId)[0];
          if (!json || !Object.keys(json).includes("expire")) {
            setLocalStorageData(data);
            setCurrentCount(0);
            return;
          }
          if (json.expire > Date.now()) {
            setCurrentCount(Math.max(json.likeCount, 0));
          }
        }
      }
    }
  }, [sessionStatus, episodeId]);

  const postLike = async () => {
    if (currentCount >= 10) return;
    setCurrentCount(currentCount + 1);
    setCurrentTotalCount(currentTotalCount + 1);
    try {
      const res = await fetch(`/api/episodes/${episodeId}/like`, { method: "POST" });
      if (res.status !== 200) throw new Error();
      if (!session?.user) {
        // 非ログイン時はlocalstorageにいいねした回数を保存
        setCountToLocalStorage(currentCount + 1, episodeId, localStorageData);
      }
    } catch (error) {
      setCurrentCount((c) => c - 1);
      setCurrentTotalCount((c) => c - 1);
      if (error instanceof Error) {
        logError("Failed to post like episode" + error.message);
      } else {
        logError("Failed to post like episode: unknown error");
      }
    }
  };

  return (
    <div
      onClick={postLike}
      title="いいね！ありがとうございます"
      className="ep-detail-react-item mode-like"
    >
      <span>
        <span className="ep-detail-react-item-number mode-user">
          {currentCount < 0 ? "-" : currentCount}/{MAX_COUNT}回
        </span>
        <Image
          src="/images/icons/like_white.png"
          width={47}
          height={43}
          className="ep-detail-react-item-logo mode-like"
          alt="いいね！"
        />
      </span>
      <span>いいね！</span>
      <span className="ep-detail-react-item-number mode-like">{currentTotalCount}</span>
    </div>
  );
}
